import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Package, DollarSign, Eye, MessageSquare, AlertTriangle,
  Loader2, Pencil, ArrowUpDown, Sparkles, Box, Globe2, Bookmark, ShieldCheck,
} from 'lucide-react';
import { API } from '../App';
import { devError } from '../utils/logger';

/**
 * Inventory snapshot for the supplier dashboard.
 *
 * Pulls a single `/api/supplier/inventory` payload (summary + by-category +
 * full per-listing rows) so the table renders without a second fetch. The
 * "stale" flag = available listing, no views in the last 30 days — purely
 * advisory, lets a supplier triage what to refresh.
 */

const STATUS_TONE = {
  available: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  sold:      'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

const aud = (n) =>
  `$${Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

function StatCard({ icon: Icon, label, value, hint, tone = 'accent', testId }) {
  const tones = {
    accent:  'text-accent',
    emerald: 'text-emerald-400',
    amber:   'text-amber-400',
    muted:   'text-muted-foreground',
  };
  return (
    <div className="bg-card border border-border/40 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-2">
        <Icon className={`w-4 h-4 ${tones[tone]}`} />
        {label}
      </div>
      <div className="font-playfair text-3xl font-bold">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function CategoryBreakdown({ rows }) {
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div className="bg-card border border-border/40 rounded-xl p-5" data-testid="inventory-by-category">
      <h3 className="font-playfair text-lg font-semibold mb-4">Mix by category</h3>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.category} className="flex items-center gap-3 text-sm">
            <div className="w-32 capitalize text-muted-foreground truncate">{r.category}</div>
            <div className="flex-1 h-2 rounded-full bg-secondary/60 overflow-hidden">
              <div
                className="h-full bg-accent/70 rounded-full"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
            <div className="w-12 text-right font-mono text-xs">{r.count}</div>
            <div className="w-20 text-right font-mono text-xs text-muted-foreground">{aud(r.value_aud)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SORT_KEYS = {
  title:             (a, b) => (a.title || '').localeCompare(b.title || ''),
  category:          (a, b) => (a.category || '').localeCompare(b.category || ''),
  price:             (a, b) => (a.price || 0) - (b.price || 0),
  quantity:          (a, b) => (a.quantity || 0) - (b.quantity || 0),
  status:            (a, b) => (a.status || '').localeCompare(b.status || ''),
  views:             (a, b) => (a.views || 0) - (b.views || 0),
  inquiries:         (a, b) => (a.inquiries || 0) - (b.inquiries || 0),
  saved_in_projects: (a, b) => (a.saved_in_projects || 0) - (b.saved_in_projects || 0),
  updated_at:        (a, b) => (a.updated_at || '').localeCompare(b.updated_at || ''),
};

export default function InventoryPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showStaleOnly, setShowStaleOnly] = useState(false);
  const [sort, setSort] = useState({ key: 'updated_at', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/supplier/inventory`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        devError('inventory load failed', err);
        if (!cancelled) setError(err.response?.data?.detail || 'Could not load inventory');
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.listings;
    if (statusFilter !== 'all') rows = rows.filter((r) => r.status === statusFilter);
    if (showStaleOnly) rows = rows.filter((r) => r.is_stale);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) =>
      (r.title || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q),
    );
    const sorter = SORT_KEYS[sort.key] || SORT_KEYS.updated_at;
    rows = [...rows].sort(sorter);
    if (sort.dir === 'desc') rows.reverse();
    return rows;
  }, [data, statusFilter, search, showStaleOnly, sort]);

  const toggleSort = (key) => {
    setSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  if (error) {
    return <div className="bg-card border border-rose-500/30 rounded-xl p-6 text-rose-300" data-testid="inventory-error">{error}</div>;
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="inventory-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading inventory…
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="inventory-panel">
      {/* Passport-quality nudge — shown when fewer than 100% of listings
          are Passport-quality. Designers filter for this; every listing
          without salvage_origin is invisible to the GSF/Climate Active
          filter. Cheap retention driver. */}
      {s.total > 0 && (s.passport_quality_count ?? 0) < s.total && (
        <div
          className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3"
          data-testid="passport-quality-nudge"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm md:text-base text-emerald-100">
                {s.passport_quality_count ?? 0} of {s.total} listings are Passport quality
              </p>
              <p className="text-xs text-emerald-200/70 mt-0.5">
                Add <strong>Salvage origin</strong> (building, year, city) to unlock Green Star Fitouts,
                Climate Active and NABERS evidence — designers filter for this.
              </p>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-emerald-300/80 md:ml-3">
            {s.total > 0 ? Math.round(((s.passport_quality_count ?? 0) / s.total) * 100) : 0}%
          </span>
        </div>
      )}

      {/* Top stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Package}      label="Total listings"   value={s.total}                                hint={`${s.available} live · ${s.sold} sold · ${s.pending} pending`} testId="stat-total"     tone="accent" />
        <StatCard icon={DollarSign}   label="Inventory value"  value={aud(s.inventory_value_aud)}             hint="At list price (available stock)"                                  testId="stat-value"     tone="emerald" />
        <StatCard icon={Eye}          label="Total views"      value={s.total_views.toLocaleString('en-AU')}  hint={`${s.total_inquiries} buyer enquiries received`}                  testId="stat-views"     tone="muted" />
        <StatCard icon={Bookmark}     label="Saved by buyers"  value={s.total_project_saves || 0}             hint="Across designer projects (leading interest signal)"               testId="stat-project-saves" tone={(s.total_project_saves || 0) > 0 ? 'accent' : 'muted'} />
        <StatCard icon={AlertTriangle} label="Needs attention" value={s.stale_count}                          hint="Live, 30 + days old, no views" testId="stat-stale" tone={s.stale_count ? 'amber' : 'muted'} />
      </div>

      {/* Category mix */}
      <CategoryBreakdown rows={data.by_category} />

      {/* Per-listing table */}
      <div className="bg-card border border-border/40 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border/40 flex flex-wrap gap-3 items-center">
          <h3 className="font-playfair text-lg font-semibold mr-auto">Stock</h3>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or category…"
            className="px-3 py-2 bg-secondary border border-border/40 rounded-md text-sm w-48"
            data-testid="inventory-search"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-secondary border border-border/40 rounded-md text-sm"
            data-testid="inventory-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="available">Available</option>
            <option value="pending">Pending</option>
            <option value="sold">Sold</option>
          </select>
          <label className="text-xs text-muted-foreground inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={showStaleOnly}
              onChange={(e) => setShowStaleOnly(e.target.checked)}
              data-testid="inventory-stale-toggle"
            />
            Stale only
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border/40">
              <tr>
                <th className="text-left p-3 font-normal">Listing</th>
                <Th label="Category"  k="category"   sort={sort} onClick={toggleSort} />
                <Th label="Price"     k="price"      sort={sort} onClick={toggleSort} className="text-right" />
                <Th label="Qty"       k="quantity"   sort={sort} onClick={toggleSort} className="text-right" />
                <Th label="Status"    k="status"     sort={sort} onClick={toggleSort} />
                <Th label="Views"     k="views"             sort={sort} onClick={toggleSort} className="text-right" />
                <Th label="Enq."      k="inquiries"         sort={sort} onClick={toggleSort} className="text-right" />
                <Th label="Saves"     k="saved_in_projects" sort={sort} onClick={toggleSort} className="text-right" />
                <Th label="Updated"   k="updated_at"        sort={sort} onClick={toggleSort} />
                <th className="text-right p-3 font-normal">Edit</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="p-12 text-center text-muted-foreground" data-testid="inventory-empty">No listings match those filters.</td></tr>
              ) : filtered.map((row) => (
                <tr
                  key={row.listing_id}
                  className="border-b border-border/20 hover:bg-secondary/20 transition-colors"
                  data-testid={`inventory-row-${row.listing_id}`}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {row.image_url ? (
                        <img src={row.image_url} alt="" className="w-10 h-10 rounded-md object-cover border border-border/40" />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-secondary border border-border/40 flex items-center justify-center text-muted-foreground"><Package className="w-4 h-4" /></div>
                      )}
                      <div className="min-w-0">
                        <Link to={`/listing/${row.listing_id}`} className="font-medium text-foreground hover:text-accent line-clamp-1">{row.title}</Link>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {row.is_featured && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-px rounded-sm bg-accent/10 text-accent border border-accent/30"><Sparkles className="w-2.5 h-2.5" />Featured</span>}
                          {row.has_digital_model && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-px rounded-sm bg-secondary text-muted-foreground border border-border/40"><Box className="w-2.5 h-2.5" />3D</span>}
                          {row.external_source && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-px rounded-sm bg-secondary text-muted-foreground border border-border/40"><Globe2 className="w-2.5 h-2.5" />{row.external_source}</span>}
                          {row.is_stale && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-px rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30"><AlertTriangle className="w-2.5 h-2.5" />Stale</span>}
                          {row.is_passport_quality && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-px rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" title="Passport quality — carbon + salvage origin documented"><ShieldCheck className="w-2.5 h-2.5" />Passport</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground capitalize">{row.category}</td>
                  <td className="p-3 text-right font-mono">{aud(row.price)}<span className="text-xs text-muted-foreground">/{row.unit}</span></td>
                  <td className="p-3 text-right font-mono">{row.quantity}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_TONE[row.status] || 'border-border/40 text-muted-foreground'}`}>{row.status}</span></td>
                  <td className="p-3 text-right font-mono"><span className="inline-flex items-center gap-1 text-muted-foreground"><Eye className="w-3 h-3" />{row.views}</span></td>
                  <td className="p-3 text-right font-mono"><span className="inline-flex items-center gap-1 text-muted-foreground"><MessageSquare className="w-3 h-3" />{row.inquiries}</span></td>
                  <td className="p-3 text-right font-mono">
                    <span className={`inline-flex items-center gap-1 ${row.saved_in_projects > 0 ? 'text-accent' : 'text-muted-foreground'}`}>
                      <Bookmark className="w-3 h-3" />{row.saved_in_projects || 0}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}</td>
                  <td className="p-3 text-right">
                    <Link to={`/edit-listing/${row.listing_id}`} className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-secondary text-muted-foreground hover:text-accent" data-testid={`inventory-edit-${row.listing_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ label, k, sort, onClick, className = '' }) {
  const active = sort.key === k;
  return (
    <th className={`p-3 font-normal cursor-pointer select-none ${className}`} onClick={() => onClick(k)}>
      <span className={`inline-flex items-center gap-1 ${active ? 'text-foreground' : ''}`}>
        {label}
        <ArrowUpDown className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-30'}`} />
        {active && <span className="text-[10px]">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );
}
